import { useState, useMemo, useCallback } from 'react';

/**
 * Custom hook for managing "View More/Less" functionality for lists/arrays
 * Different from useExpandable which handles text truncation
 * 
 * @param {Array} items - Full array of items to display
 * @param {number} initialLimit - Maximum number of items to show when collapsed (can be from CMS)
 * @returns {object} View more state and utilities
 * 
 * @example
 * // Basic usage with hardcoded limit
 * const { visibleItems, isExpanded, needsExpansion, toggle } = useViewMoreToggle(features, 3);
 * 
 * @example
 * // With CMS-configured limit
 * const { visibleItems, isExpanded, needsExpansion, toggle } = useViewMoreToggle(
 *   products, 
 *   maxProductsToShow || 6  // CMS field with fallback
 * );
 */
function useViewMoreToggle(items, initialLimit = 3) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Calculate if expansion is needed
  const needsExpansion = useMemo(() => {
    if (!Array.isArray(items)) return false;
    return items.length > initialLimit;
  }, [items, initialLimit]);

  // Get visible items based on expanded state
  const visibleItems = useMemo(() => {
    if (!Array.isArray(items)) return [];
    if (!needsExpansion || isExpanded) return items;
    return items.slice(0, initialLimit);
  }, [items, isExpanded, needsExpansion, initialLimit]);

  // Count of remaining items when collapsed
  const remainingCount = useMemo(() => {
    if (!needsExpansion || isExpanded) return 0;
    return items.length - initialLimit;
  }, [items, initialLimit, needsExpansion, isExpanded]);

  // Toggle function
  const toggle = useCallback(() => {
    setIsExpanded(prev => !prev);
  }, []);

  // Explicit setters for advanced use cases
  const expand = useCallback(() => setIsExpanded(true), []);
  const collapse = useCallback(() => setIsExpanded(false), []);

  return {
    visibleItems,        // Array of items to display
    isExpanded,          // Boolean: is currently expanded
    needsExpansion,      // Boolean: does list need view more button
    remainingCount,      // Number: how many items are hidden
    toggle,              // Function: toggle expanded state
    expand,              // Function: expand to show all
    collapse,            // Function: collapse to initial limit
    totalCount: items?.length || 0,  // Total number of items
  };
}

export default useViewMoreToggle;
